/**
 * El canje del enlace · `/acceso?token=…` · `BFF_BO.md` §4
 *
 * Tiene que aguantar que **el cliente de correo abra el enlace antes que la
 * persona**: el token es de un solo uso, así que cuando llega
 * `410 magic_link_expired` se ofrece pedir otro **sin dramatismo**. No es un
 * error de la persona: es Outlook precargando enlaces.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '@/shared/api/client';
import { aProblema, type Problema } from '@/shared/api/errores';
import { useSesion } from '@/shared/auth/sesionContexto';
import { Cargando } from '@/shared/ui/Estados';

export function Canje({ token }: { token: string }) {
  const { entrar } = useSesion();
  const [problema, setProblema] = useState<Problema | null>(null);
  // El token es de un solo uso: si React monta dos veces —y en desarrollo lo
  // hace—, el segundo canje lo quema y la persona ve un error que no existe.
  const canjeado = useRef(false);

  useEffect(() => {
    if (canjeado.current) return;
    canjeado.current = true;

    void (async () => {
      const { data, error } = await api.v1.auth['magic-link'].verify.post({
        token,
      });

      if (error || !data) {
        setProblema(aProblema(error));
        return;
      }

      // Se limpia el token de la URL antes de seguir: queda en el historial, y
      // de ahí en una captura de pantalla que alguien pega en un chat.
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState(null, '', url);

      await entrar((data as unknown as { access_token: string }).access_token);
    })();
  }, [token, entrar]);

  if (!problema) return <Cargando filas={2} etiqueta="Entrando" />;

  const caducado =
    problema.status === 410 || problema.code === 'magic_link_expired';

  return (
    <main className="acceso">
      <div className="tarjeta">
        <h1>{caducado ? 'Ese enlace ya se usó' : problema.title}</h1>
        <p>
          {caducado
            ? 'Los enlaces valen una sola vez y caducan en 15 minutos. A veces los gasta el propio programa de correo al previsualizarlo. Pide otro y listo.'
            : problema.detail}
        </p>
        <button type="button" onClick={() => window.location.assign('/')}>
          Pedir otro enlace
        </button>
      </div>
    </main>
  );
}
